import { Link } from "@tanstack/react-router";
import { Topbar } from "./Topbar";
import { Footer } from "./Footer";
import { Hero } from "./Hero";
import { Section } from "./Section";
import { CTABlock } from "./CTABlock";
import { RevealOnScroll } from "./RevealOnScroll";
import { REGION_ORDER, REGIONS } from "@/lib/regions-data";
import { pathMap } from "@/lib/locale";

type Props = { locale: "de" | "en" };

export function RegionsOverview({ locale }: Props) {
  const isEn = locale === "en";

  const copy = isEn
    ? {
        eyebrow: "Regions",
        headline: "Regions — ",
        headlineEm: "an address in Karlstadt am Main, methodology nationwide.",
        lead: "Calvarius works from Karlstadt am Main. From there we serve mandates in person within a 100 km radius — Würzburg, Schweinfurt, Aschaffenburg, Frankfurt am Main, Nürnberg — and digitally everywhere else in DACH. The pages below describe how working with a regional client typically runs and what economic context shapes the mandates we see in each region.",
        meta: ["Address: Karlstadt am Main", "Service area: nationwide", "MD on every account"] as [string, string, string],
        kicker: "Service area",
        sectionHead: "Five regions where in-person presence matters",
        sectionCaption:
          "Calvarius works from one address in Karlstadt am Main. We organize in-person appointments where they matter for the mandate — we plan logistics around need, not around postal code. The following regions see this most often, because they belong to our core service area.",
        nationwideKicker: "Nationwide and international",
        nationwideHead: "Nationwide, DACH-wide, ",
        nationwideHeadEm: "and occasionally beyond.",
        nationwideBody:
          "Mandates outside the region we manage digitally — with clear documentation, shared tracking, and structured reviews. That's how we've worked since 2013 with clients across Germany, Austria, and Switzerland. When a project requires in-person presence, we've also been on-site internationally — in Spain, the UK, or Hungary, for example. The question is never where the mandate is located, but what it economically needs.",
        ctaHead: "Not sure if your region is a fit?",
        ctaBody:
          "If you're outside the listed regions, that's not a blocker — it's the default. Most of our mandates are managed digitally. If you're in DACH and looking for a senior performance partner, the conversation works the same way.",
        ctaPrimary: "Start a conversation",
        ctaSecondary: "All services",
        breadcrumbs: { home: "Start" },
        cardKm: "km",
        cardMin: "min",
        learnMore: "Region details ↗",
      }
    : {
        eyebrow: "Regionen",
        headline: "Regionen — ",
        headlineEm: "eine Adresse in Karlstadt am Main, Methodik bundesweit.",
        lead: "Calvarius arbeitet aus Karlstadt am Main. Von dort betreuen wir Mandate persönlich im 100-Kilometer-Radius — Würzburg, Schweinfurt, Aschaffenburg, Frankfurt am Main, Nürnberg — und digital überall sonst in DACH. Die folgenden Seiten beschreiben, wie die Zusammenarbeit mit regionalen Mandaten typischerweise abläuft und welcher wirtschaftliche Kontext den Mandate-Mix in der jeweiligen Region prägt.",
        meta: ["Adresse: Karlstadt am Main", "Einzugsgebiet: bundesweit", "GF operativ"] as [string, string, string],
        kicker: "Einzugsgebiet",
        sectionHead: "Fünf Regionen, in denen persönliche Präsenz wirtschaftlich ist",
        sectionCaption:
          "Calvarius arbeitet aus einer Adresse in Karlstadt am Main. Persönliche Termine organisieren wir dort, wo sie für den Mandate-Verlauf wichtig sind — die Logistik richten wir am Bedarf aus, nicht am Postleitzahl-Bezirk. In folgenden Regionen passiert das besonders oft, weil sie zu unserem Kern-Einzugsgebiet gehören.",
        nationwideKicker: "Bundesweit und international",
        nationwideHead: "Bundesweit, DACH-weit, ",
        nationwideHeadEm: "gelegentlich darüber hinaus.",
        nationwideBody:
          "Mandate außerhalb der Region betreuen wir digital — mit klarer Dokumentation, geteiltem Tracking und strukturierten Reviews. So arbeiten wir seit 2013 mit Mandanten in ganz Deutschland, Österreich und der Schweiz. Wenn ein Projekt persönliche Anwesenheit verlangt, sind wir auch international vor Ort gewesen — etwa in Spanien, UK oder Ungarn. Die Frage ist nie, wo der Mandant sitzt, sondern was er wirtschaftlich braucht.",
        ctaHead: "Unsicher, ob Ihre Region passt?",
        ctaBody:
          "Wenn Sie außerhalb der gelisteten Regionen sitzen, ist das kein Hindernis — sondern der Normalfall. Die Mehrzahl unserer Mandate wird digital geführt. Wenn Sie in DACH sitzen und einen Senior-Performance-Partner suchen, läuft das Gespräch identisch ab.",
        ctaPrimary: "Erst-Gespräch anfragen",
        ctaSecondary: "Alle Leistungen",
        breadcrumbs: { home: "Start" },
        cardKm: "km",
        cardMin: "min",
        learnMore: "Region ansehen ↗",
      };

  return (
    <>
      <Topbar />
      <main id="main">
        <Hero
          number="01"
          eyebrow={copy.eyebrow}
          headline={
            <>
              {copy.headline}
              <em>{copy.headlineEm}</em>
            </>
          }
          lead={copy.lead}
          metaStrip={[...copy.meta]}
          breadcrumbs={[
            { label: copy.breadcrumbs.home, to: isEn ? "/en" : "/" },
            { label: copy.eyebrow },
          ]}
        />

        <Section
          number="02"
          kicker={copy.kicker}
          headline={<em>{copy.sectionHead}.</em>}
          caption={copy.sectionCaption}
        >
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {REGION_ORDER.map((slug, i) => {
              const r = REGIONS[slug];
              const href = isEn ? `/en/regions/${slug}` : `/regionen/${slug}`;
              const isWuerzburg = slug === "wuerzburg" && !isEn;
              return (
                <RevealOnScroll key={slug} delay={i * 60}>
                  <div className="group flex h-full flex-col rounded-lg border border-rule bg-white p-7 transition-colors hover:border-cyan-deep/50">
                    <Link to={href} className="flex flex-1 flex-col">
                      <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ink-4">
                        {r.distanceKm} {copy.cardKm} · {r.driveMinutes} {copy.cardMin}
                      </div>
                      <h3 className="mt-3 font-display text-2xl leading-tight text-ink">
                        {r.cityName}
                      </h3>
                      <p className="mt-4 leading-relaxed text-ink-3 flex-1">
                        {r.cardLead[locale]}
                      </p>
                      <div className="mt-5 font-mono text-xs uppercase tracking-wider text-cyan-deep group-hover:underline">
                        {copy.learnMore}
                      </div>
                    </Link>
                    {isWuerzburg && (
                      <Link
                        to="/regionen/wuerzburg/seo-agentur"
                        className="mt-3 border-t border-rule pt-3 font-mono text-xs uppercase tracking-wider text-ink-3 hover:text-cyan-deep hover:underline"
                      >
                        SEO-Agentur Würzburg →
                      </Link>
                    )}
                  </div>
                </RevealOnScroll>
              );
            })}

          </div>
        </Section>

        <Section
          number="03"
          variant="tinted"
          kicker={copy.nationwideKicker}
          headline={<>{copy.nationwideHead}<em>{copy.nationwideHeadEm}</em></>}
        >
          <div className="max-w-3xl">
            <p className="text-lg leading-relaxed text-ink-2">{copy.nationwideBody}</p>
          </div>
        </Section>

        <Section number="04" variant="deep">
          <CTABlock
            variant="deep"
            headline={<em>{copy.ctaHead}</em>}
            body={copy.ctaBody}
            primary={{ label: copy.ctaPrimary, to: isEn ? pathMap.contact.en : pathMap.contact.de }}
            secondary={{ label: copy.ctaSecondary, to: isEn ? pathMap.services.en : pathMap.services.de }}
          />
        </Section>
      </main>
      <Footer />
    </>
  );
}
